// knownPages — the flat list of every visitor-facing route in the app
// used by /admin/text to render one dashboard section per real page
// (including pages that haven't been scanned or edited yet, so they
// appear with 0 slots and a "Not scanned yet" indicator).
//
// This file intentionally does NOT depend on `routesRegistry.jsx` to
// avoid a circular import (`routesRegistry` imports every page
// component, including `AdminTextPage`, which then reads this list).
// The path list here is kept in lock-step with the public + editorial
// routes declared in `routesRegistry.jsx` — add a new visitor route in
// both places when introducing a new page.
//
// Parameterised routes (`/hotels/:slug`, `/experiences/:key`,
// `/blog/:slug`) are expanded here using the real data files so every
// individual detail page becomes its own dashboard section.

import { HOTELS } from "@/lib/data";
import { EXPERIENCE_DETAILS } from "@/lib/experienceDetails";
import { BLOG_POSTS } from "@/lib/blogData";

const STATIC_PUBLIC = [
  { path: "/",                  label: "Homepage",             emoji: "🏡" },
  { path: "/southern-morocco",  label: "Southern Morocco",     emoji: "🗺" },
  { path: "/hotels",            label: "Hotels — index",       emoji: "🛏" },
  { path: "/bivouacs",          label: "Bivouacs — index",     emoji: "⛺" },
  { path: "/experiences",       label: "Experiences — index",  emoji: "🗺" },
  { path: "/trips",             label: "Trips",                emoji: "🚌" },
  { path: "/gastronomy",        label: "Gastronomy",           emoji: "🍽" },
  { path: "/events",            label: "Events",               emoji: "🎼" },
  { path: "/packs",             label: "Packs",                emoji: "🎁" },
  { path: "/pricing",           label: "Pricing",              emoji: "💵" },
  { path: "/booking",           label: "Booking (enquiry)",    emoji: "📅" },
  { path: "/classic-booking",   label: "Booking — classic",    emoji: "📅" },
  { path: "/contact",           label: "Contact",              emoji: "✉️" },
  { path: "/directions",        label: "Directions",           emoji: "🧭" },
];

const STATIC_EDITORIAL = [
  { path: "/about",             label: "About",                emoji: "📖" },
  { path: "/wall-of-love",      label: "Wall of love",         emoji: "💌" },
  { path: "/press",             label: "Press",                emoji: "🗞" },
  { path: "/partners",          label: "Partners",             emoji: "🤝" },
  { path: "/blog",              label: "Blog — index",         emoji: "🪶" },
  { path: "/brochures",         label: "Brochures",            emoji: "📚" },
  { path: "/team",              label: "Team",                 emoji: "👥" },
  { path: "/knowledgebase",     label: "Knowledge base",       emoji: "🎓" },
  { path: "/work-with-us",      label: "Work with us",         emoji: "💼" },
];

const buildKnownPages = () => {
  const out = [];
  STATIC_PUBLIC.forEach((p) => out.push({ ...p, category: "public" }));
  STATIC_EDITORIAL.forEach((p) => out.push({ ...p, category: "editorial" }));
  HOTELS.forEach((h) => {
    out.push({
      path: `/hotels/${h.slug}`,
      label: `Hotel · ${h.name}`,
      emoji: "🛏",
      category: "public",
    });
  });
  Object.entries(EXPERIENCE_DETAILS).forEach(([key, exp]) => {
    out.push({
      path: `/experiences/${key}`,
      label: `Experience · ${exp?.title?.en || key}`,
      emoji: "🗺",
      category: "public",
    });
  });
  BLOG_POSTS.forEach((p) => {
    out.push({
      path: `/blog/${p.slug}`,
      label: `Blog · ${p?.title?.en || p.slug}`,
      emoji: "🪶",
      category: "editorial",
    });
  });
  return out;
};

export const KNOWN_PAGES = buildKnownPages();
export const KNOWN_PATHS = new Set(KNOWN_PAGES.map((p) => p.path));
export const KNOWN_META = new Map(KNOWN_PAGES.map((p) => [p.path, p]));
